import { useState, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Divider,
    Slide,
    Alert,
    Snackbar,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import React from 'react';
import { useStoreConfig } from '../../../../../contexts/StoreConfigContext';
import { RestaurantSubmissionService } from '../../../../../services/RestaurantSubmissionService';
import {
    BusinessType,
    PaymentTerms,
    CreateRestaurantSubmissionDto,
} from '../../../../../models/DB';

// Section Header component - defined outside to prevent re-creation on each render
const SectionHeader = ({
    icon,
    title,
    primaryColor,
}: {
    icon: React.ReactNode;
    title: string;
    primaryColor: string;
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            marginBottom: 2,
            marginTop: 3,
        }}
    >
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: `${primaryColor}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: primaryColor,
            }}
        >
            {icon}
        </Box>
        <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {title}
        </Typography>
    </Box>
);

// Form Row component - defined outside to prevent re-creation on each render
const FormRow = ({
    children,
    isMobile,
}: {
    children: React.ReactNode;
    isMobile: boolean;
}) => (
    <Box
        sx={{
            display: 'flex',
            gap: 2,
            flexDirection: isMobile ? 'column' : 'row',
            marginBottom: 2,
        }}
    >
        {children}
    </Box>
);

interface RestaurantSignupFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

// Slide transition for mobile fullscreen
const SlideTransition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    return <Slide direction='up' ref={ref} {...props} />;
});

type FormData = {
    // Business Type
    business_type: BusinessType | '';

    // Business Information
    registered_name: string;
    trading_name: string;
    principal_address: string;
    principal_postal_code: string;
    delivery_address: string;
    delivery_postal_code: string;
    registration_number: string;
    date_commenced: string;
    vat_number: string;
    registered_office: string;
    registered_office_postal_code: string;
    telephone: string;
    mobile: string;
    accept_back_orders: boolean;

    // Accounts Department
    accounts_contact_name: string;
    accounts_email: string;

    // Buying Department
    buying_contact_name: string;
    buying_email: string;

    // Owner 1
    owner1_full_names: string;
    owner1_id_number: string;
    owner1_dob: string;
    owner1_address: string;
    owner1_postal_code: string;
    owner1_telephone: string;
    owner1_mobile: string;

    // Owner 2
    owner2_full_names: string;
    owner2_id_number: string;
    owner2_dob: string;
    owner2_address: string;
    owner2_postal_code: string;
    owner2_telephone: string;
    owner2_mobile: string;

    // Banking
    bank_name: string;
    branch_code: string;
    branch_name: string;
    account_number: string;

    // Payment Terms
    payment_terms: PaymentTerms | '';
};

const initialFormData: FormData = {
    business_type: '',
    registered_name: '',
    trading_name: '',
    principal_address: '',
    principal_postal_code: '',
    delivery_address: '',
    delivery_postal_code: '',
    registration_number: '',
    date_commenced: '',
    vat_number: '',
    registered_office: '',
    registered_office_postal_code: '',
    telephone: '',
    mobile: '',
    accept_back_orders: false,
    accounts_contact_name: '',
    accounts_email: '',
    buying_contact_name: '',
    buying_email: '',
    owner1_full_names: '',
    owner1_id_number: '',
    owner1_dob: '',
    owner1_address: '',
    owner1_postal_code: '',
    owner1_telephone: '',
    owner1_mobile: '',
    owner2_full_names: '',
    owner2_id_number: '',
    owner2_dob: '',
    owner2_address: '',
    owner2_postal_code: '',
    owner2_telephone: '',
    owner2_mobile: '',
    bank_name: '',
    branch_code: '',
    branch_name: '',
    account_number: '',
    payment_terms: '',
};

const businessTypes: { value: BusinessType; label: string }[] = [
    { value: 'PTY_LTD', label: 'PTY (LTD)' },
    { value: 'LTD', label: 'LTD' },
    { value: 'CC', label: 'CC' },
    { value: 'SOLE_OWNER', label: 'SOLE OWNER' },
    { value: 'CO_OP', label: 'CO-OP' },
    { value: 'P_SHIP', label: 'P/SHIP' },
    { value: 'OTHER', label: 'OTHER' },
];

const paymentTermsOptions: { value: PaymentTerms; label: string }[] = [
    { value: 'in_advance', label: 'In Advance' },
    { value: 'cod', label: 'C.O.D.' },
    { value: '7_days', label: '7 Days' },
    { value: '14_days', label: '14 Days' },
];

export const RestaurantSignupForm = ({
    open,
    onClose,
    onSuccess,
}: RestaurantSignupFormProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { config } = useStoreConfig();

    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleChange = (field: keyof FormData) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value =
            event.target.type === 'checkbox'
                ? event.target.checked
                : event.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        // Validate required fields
        if (!formData.business_type) {
            setError('Please select a business type');
            return;
        }
        if (!formData.registered_name.trim()) {
            setError('Registered name is required');
            return;
        }
        if (!formData.principal_address.trim()) {
            setError('Principal address is required');
            return;
        }
        if (!formData.delivery_address.trim()) {
            setError('Delivery address is required');
            return;
        }
        if (!formData.mobile.trim()) {
            setError('Mobile number is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const submissionData: CreateRestaurantSubmissionDto = {
                business_type: formData.business_type as BusinessType,
                registered_name: formData.registered_name,
                trading_name: formData.trading_name || undefined,
                principal_address: formData.principal_address,
                principal_postal_code: formData.principal_postal_code || undefined,
                delivery_address: formData.delivery_address,
                delivery_postal_code: formData.delivery_postal_code || undefined,
                registration_number: formData.registration_number || undefined,
                date_commenced: formData.date_commenced || undefined,
                vat_number: formData.vat_number || undefined,
                registered_office: formData.registered_office || undefined,
                registered_office_postal_code:
                    formData.registered_office_postal_code || undefined,
                telephone: formData.telephone || undefined,
                mobile: formData.mobile,
                accept_back_orders: formData.accept_back_orders,
                accounts_contact_name: formData.accounts_contact_name || undefined,
                accounts_email: formData.accounts_email || undefined,
                buying_contact_name: formData.buying_contact_name || undefined,
                buying_email: formData.buying_email || undefined,
                owner1_full_names: formData.owner1_full_names || undefined,
                owner1_id_number: formData.owner1_id_number || undefined,
                owner1_dob: formData.owner1_dob || undefined,
                owner1_address: formData.owner1_address || undefined,
                owner1_postal_code: formData.owner1_postal_code || undefined,
                owner1_telephone: formData.owner1_telephone || undefined,
                owner1_mobile: formData.owner1_mobile || undefined,
                owner2_full_names: formData.owner2_full_names || undefined,
                owner2_id_number: formData.owner2_id_number || undefined,
                owner2_dob: formData.owner2_dob || undefined,
                owner2_address: formData.owner2_address || undefined,
                owner2_postal_code: formData.owner2_postal_code || undefined,
                owner2_telephone: formData.owner2_telephone || undefined,
                owner2_mobile: formData.owner2_mobile || undefined,
                bank_name: formData.bank_name || undefined,
                branch_code: formData.branch_code || undefined,
                branch_name: formData.branch_name || undefined,
                account_number: formData.account_number || undefined,
                payment_terms: formData.payment_terms
                    ? (formData.payment_terms as PaymentTerms)
                    : undefined,
            };

            const result =
                await RestaurantSubmissionService.submitApplication(submissionData);

            if (result) {
                setSuccess(true);
                setFormData(initialFormData);
                setTimeout(() => {
                    onSuccess?.();
                    onClose();
                }, 2000);
            } else {
                setError('Failed to submit application. Please try again.');
            }
        } catch (err) {
            console.error('Submission error:', err);
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData(initialFormData);
            setError(null);
            setSuccess(false);
            onClose();
        }
    };

    // Create refs for all text fields to enable keyboard navigation
    const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

    // Field order for keyboard navigation
    const fieldOrder: (keyof FormData)[] = [
        'registered_name',
        'trading_name',
        'principal_address',
        'principal_postal_code',
        'delivery_address',
        'delivery_postal_code',
        'registration_number',
        'date_commenced',
        'vat_number',
        'registered_office',
        'registered_office_postal_code',
        'telephone',
        'mobile',
        'accounts_contact_name',
        'accounts_email',
        'buying_contact_name',
        'buying_email',
        'owner1_full_names',
        'owner1_id_number',
        'owner1_dob',
        'owner1_address',
        'owner1_postal_code',
        'owner1_telephone',
        'owner1_mobile',
        'owner2_full_names',
        'owner2_id_number',
        'owner2_dob',
        'owner2_address',
        'owner2_postal_code',
        'owner2_telephone',
        'owner2_mobile',
        'bank_name',
        'branch_code',
        'branch_name',
        'account_number',
    ];

    // Focus next field on "Enter" or "Next" key
    const focusNextField = useCallback((currentField: keyof FormData) => {
        const currentIndex = fieldOrder.indexOf(currentField);
        if (currentIndex < fieldOrder.length - 1) {
            const nextField = fieldOrder[currentIndex + 1];
            inputRefs.current[nextField]?.focus();
        }
    }, []);

    // Get common input props for keyboard navigation
    const getInputProps = (field: keyof FormData, isLast = false) => ({
        inputRef: (el: HTMLInputElement | null) => {
            inputRefs.current[field] = el;
        },
        inputProps: {
            enterKeyHint: isLast ? 'done' : 'next',
            onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' && !isLast) {
                    e.preventDefault();
                    focusNextField(field);
                }
            },
        },
    });

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={isMobile}
                maxWidth='md'
                fullWidth
                TransitionComponent={isMobile ? SlideTransition : undefined}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        maxHeight: isMobile ? '100%' : '90vh',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: config.primaryColor,
                        color: 'white',
                        padding: isMobile ? 2 : 3,
                    }}
                >
                    <Box>
                        <Typography variant='h6' sx={{ fontWeight: 600 }}>
                            Restaurant Client Application
                        </Typography>
                        <Typography variant='body2' sx={{ opacity: 0.9, marginTop: 0.5 }}>
                            Complete the form below to become a wholesale client
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        disabled={loading}
                        sx={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ padding: isMobile ? 2 : 3 }}>
                    {/* Business Type Section */}
                    <SectionHeader
                        icon={<BusinessIcon />}
                        title='Business Type'
                        primaryColor={config.primaryColor}
                    />
                    <FormControl component='fieldset'>
                        <RadioGroup
                            row={!isMobile}
                            value={formData.business_type}
                            onChange={handleChange('business_type')}
                            sx={{ gap: isMobile ? 0 : 1 }}
                        >
                            {businessTypes.map((type) => (
                                <FormControlLabel
                                    key={type.value}
                                    value={type.value}
                                    control={
                                        <Radio
                                            sx={{
                                                color: config.primaryColor,
                                                '&.Mui-checked': {
                                                    color: config.primaryColor,
                                                },
                                            }}
                                        />
                                    }
                                    label={type.label}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    <Divider sx={{ my: 3 }} />

                    {/* Business Information Section */}
                    <SectionHeader
                        icon={<BusinessIcon />}
                        title='Business Information'
                        primaryColor={config.primaryColor}
                    />
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Registered Name *'
                            value={formData.registered_name}
                            onChange={handleChange('registered_name')}
                            fullWidth
                            size='small'
                            {...getInputProps('registered_name')}
                        />
                        <TextField
                            label='Trading Name'
                            value={formData.trading_name}
                            onChange={handleChange('trading_name')}
                            fullWidth
                            size='small'
                            {...getInputProps('trading_name')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Principal Address *'
                            value={formData.principal_address}
                            onChange={handleChange('principal_address')}
                            fullWidth
                            size='small'
                            {...getInputProps('principal_address')}
                        />
                        <TextField
                            label='Postal Code'
                            value={formData.principal_postal_code}
                            onChange={handleChange('principal_postal_code')}
                            sx={{ minWidth: isMobile ? '100%' : 150 }}
                            size='small'
                            {...getInputProps('principal_postal_code')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Delivery Address *'
                            value={formData.delivery_address}
                            onChange={handleChange('delivery_address')}
                            fullWidth
                            size='small'
                            {...getInputProps('delivery_address')}
                        />
                        <TextField
                            label='Postal Code'
                            value={formData.delivery_postal_code}
                            onChange={handleChange('delivery_postal_code')}
                            sx={{ minWidth: isMobile ? '100%' : 150 }}
                            size='small'
                            {...getInputProps('delivery_postal_code')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Registration Number'
                            value={formData.registration_number}
                            onChange={handleChange('registration_number')}
                            fullWidth
                            size='small'
                            {...getInputProps('registration_number')}
                        />
                        <TextField
                            label='Date Commenced'
                            type='date'
                            value={formData.date_commenced}
                            onChange={handleChange('date_commenced')}
                            fullWidth
                            size='small'
                            InputLabelProps={{ shrink: true }}
                            {...getInputProps('date_commenced')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='VAT Number'
                            value={formData.vat_number}
                            onChange={handleChange('vat_number')}
                            fullWidth
                            size='small'
                            {...getInputProps('vat_number')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Registered Office'
                            value={formData.registered_office}
                            onChange={handleChange('registered_office')}
                            fullWidth
                            size='small'
                            {...getInputProps('registered_office')}
                        />
                        <TextField
                            label='Postal Code'
                            value={formData.registered_office_postal_code}
                            onChange={handleChange('registered_office_postal_code')}
                            sx={{ minWidth: isMobile ? '100%' : 150 }}
                            size='small'
                            {...getInputProps('registered_office_postal_code')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Telephone'
                            value={formData.telephone}
                            onChange={handleChange('telephone')}
                            fullWidth
                            size='small'
                            {...getInputProps('telephone')}
                        />
                        <TextField
                            label='Mobile *'
                            value={formData.mobile}
                            onChange={handleChange('mobile')}
                            fullWidth
                            size='small'
                            {...getInputProps('mobile')}
                        />
                    </FormRow>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.accept_back_orders}
                                onChange={handleChange('accept_back_orders')}
                                sx={{
                                    color: config.primaryColor,
                                    '&.Mui-checked': {
                                        color: config.primaryColor,
                                    },
                                }}
                            />
                        }
                        label='Accept Back Orders'
                    />

                    <Divider sx={{ my: 3 }} />

                    {/* Department Contacts */}
                    <SectionHeader
                        icon={<PersonIcon />}
                        title='Department Contacts'
                        primaryColor={config.primaryColor}
                    />
                    <Typography
                        variant='subtitle2'
                        sx={{ marginBottom: 1, fontWeight: 600 }}
                    >
                        Accounts Department
                    </Typography>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Contact Name'
                            value={formData.accounts_contact_name}
                            onChange={handleChange('accounts_contact_name')}
                            fullWidth
                            size='small'
                            {...getInputProps('accounts_contact_name')}
                        />
                        <TextField
                            label='Email'
                            type='email'
                            value={formData.accounts_email}
                            onChange={handleChange('accounts_email')}
                            fullWidth
                            size='small'
                            {...getInputProps('accounts_email')}
                        />
                    </FormRow>
                    <Typography
                        variant='subtitle2'
                        sx={{ marginBottom: 1, fontWeight: 600, marginTop: 2 }}
                    >
                        Buying Department
                    </Typography>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Contact Name'
                            value={formData.buying_contact_name}
                            onChange={handleChange('buying_contact_name')}
                            fullWidth
                            size='small'
                            {...getInputProps('buying_contact_name')}
                        />
                        <TextField
                            label='Email'
                            type='email'
                            value={formData.buying_email}
                            onChange={handleChange('buying_email')}
                            fullWidth
                            size='small'
                            {...getInputProps('buying_email')}
                        />
                    </FormRow>

                    <Divider sx={{ my: 3 }} />

                    {/* Owner 1 */}
                    <SectionHeader
                        icon={<PersonIcon />}
                        title='Owner / Director / Partner 1'
                        primaryColor={config.primaryColor}
                    />
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Full Names'
                            value={formData.owner1_full_names}
                            onChange={handleChange('owner1_full_names')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner1_full_names')}
                        />
                        <TextField
                            label='ID Number'
                            value={formData.owner1_id_number}
                            onChange={handleChange('owner1_id_number')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner1_id_number')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Date of Birth'
                            type='date'
                            value={formData.owner1_dob}
                            onChange={handleChange('owner1_dob')}
                            fullWidth
                            size='small'
                            InputLabelProps={{ shrink: true }}
                            {...getInputProps('owner1_dob')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Address'
                            value={formData.owner1_address}
                            onChange={handleChange('owner1_address')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner1_address')}
                        />
                        <TextField
                            label='Postal Code'
                            value={formData.owner1_postal_code}
                            onChange={handleChange('owner1_postal_code')}
                            sx={{ minWidth: isMobile ? '100%' : 150 }}
                            size='small'
                            {...getInputProps('owner1_postal_code')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Telephone'
                            value={formData.owner1_telephone}
                            onChange={handleChange('owner1_telephone')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner1_telephone')}
                        />
                        <TextField
                            label='Mobile'
                            value={formData.owner1_mobile}
                            onChange={handleChange('owner1_mobile')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner1_mobile')}
                        />
                    </FormRow>

                    <Divider sx={{ my: 3 }} />

                    {/* Owner 2 (Optional) */}
                    <SectionHeader
                        icon={<PersonIcon />}
                        title='Owner / Director / Partner 2 (Optional)'
                        primaryColor={config.primaryColor}
                    />
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Full Names'
                            value={formData.owner2_full_names}
                            onChange={handleChange('owner2_full_names')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner2_full_names')}
                        />
                        <TextField
                            label='ID Number'
                            value={formData.owner2_id_number}
                            onChange={handleChange('owner2_id_number')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner2_id_number')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Date of Birth'
                            type='date'
                            value={formData.owner2_dob}
                            onChange={handleChange('owner2_dob')}
                            fullWidth
                            size='small'
                            InputLabelProps={{ shrink: true }}
                            {...getInputProps('owner2_dob')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Address'
                            value={formData.owner2_address}
                            onChange={handleChange('owner2_address')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner2_address')}
                        />
                        <TextField
                            label='Postal Code'
                            value={formData.owner2_postal_code}
                            onChange={handleChange('owner2_postal_code')}
                            sx={{ minWidth: isMobile ? '100%' : 150 }}
                            size='small'
                            {...getInputProps('owner2_postal_code')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Telephone'
                            value={formData.owner2_telephone}
                            onChange={handleChange('owner2_telephone')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner2_telephone')}
                        />
                        <TextField
                            label='Mobile'
                            value={formData.owner2_mobile}
                            onChange={handleChange('owner2_mobile')}
                            fullWidth
                            size='small'
                            {...getInputProps('owner2_mobile')}
                        />
                    </FormRow>

                    <Divider sx={{ my: 3 }} />

                    {/* Banking Details */}
                    <SectionHeader
                        icon={<AccountBalanceIcon />}
                        title='Banking Details'
                        primaryColor={config.primaryColor}
                    />
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Bank Name'
                            value={formData.bank_name}
                            onChange={handleChange('bank_name')}
                            fullWidth
                            size='small'
                            {...getInputProps('bank_name')}
                        />
                        <TextField
                            label='Branch Code'
                            value={formData.branch_code}
                            onChange={handleChange('branch_code')}
                            fullWidth
                            size='small'
                            {...getInputProps('branch_code')}
                        />
                    </FormRow>
                    <FormRow isMobile={isMobile}>
                        <TextField
                            label='Branch Name'
                            value={formData.branch_name}
                            onChange={handleChange('branch_name')}
                            fullWidth
                            size='small'
                            {...getInputProps('branch_name')}
                        />
                        <TextField
                            label='Account Number'
                            value={formData.account_number}
                            onChange={handleChange('account_number')}
                            fullWidth
                            size='small'
                            {...getInputProps('account_number', true)}
                        />
                    </FormRow>

                    <Divider sx={{ my: 3 }} />

                    {/* Payment Terms */}
                    <SectionHeader icon={<PaymentsIcon />} title='Payment Terms' primaryColor={config.primaryColor} />
                    <FormControl component='fieldset'>
                        <RadioGroup
                            row={!isMobile}
                            value={formData.payment_terms}
                            onChange={handleChange('payment_terms')}
                            sx={{ gap: isMobile ? 0 : 2 }}
                        >
                            {paymentTermsOptions.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={
                                        <Radio
                                            sx={{
                                                color: config.primaryColor,
                                                '&.Mui-checked': {
                                                    color: config.primaryColor,
                                                },
                                            }}
                                        />
                                    }
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    {error && (
                        <Alert severity='error' sx={{ marginTop: 3 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        padding: isMobile ? 2 : 3,
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{
                            backgroundColor: config.primaryColor,
                            '&:hover': {
                                backgroundColor: config.primaryColor,
                                filter: 'brightness(0.9)',
                            },
                            paddingX: 4,
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={24} color='inherit' />
                        ) : (
                            'Submit Application'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={success}
                autoHideDuration={4000}
                onClose={() => setSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity='success'
                    variant='filled'
                    onClose={() => setSuccess(false)}
                >
                    Application submitted successfully! We will be in touch soon.
                </Alert>
            </Snackbar>
        </>
    );
};

export default RestaurantSignupForm;
